import { cn } from '@/lib/cn';
import { getFamilySiteLucideIcon } from '@/lib/family-site-icons';
import type { PublicFamilySite } from '@innermate/shared';
import { Globe } from 'lucide-react';

interface FamilySitesSectionProps {
  sites: PublicFamilySite[];
}

/**
 * 채팅 사이드바의 "패밀리 사이트" 그룹 본문.
 * AppSidebar 의 SidebarItem 은 icon: LucideIcon 만 받아서, 이모지·이미지 아이콘을
 * 자유롭게 그리기 위해 renderContent 로 직접 <ul> 을 구성.
 */
export function FamilySitesSection({ sites }: FamilySitesSectionProps) {
  if (sites.length === 0) return null;
  return (
    <ul className="space-y-0.5">
      {sites.map((site) => (
        <li key={site.id}>
          <a
            href={site.url}
            target={site.openInNewTab ? '_blank' : undefined}
            rel={site.openInNewTab ? 'noopener noreferrer' : undefined}
            className={cn(
              'flex items-center gap-2.5 h-9 px-2.5 rounded-md text-sm w-full',
              'text-foreground-muted hover:bg-surface-muted hover:text-foreground transition-colors',
            )}
          >
            <FamilySiteIcon site={site} />
            <span className="truncate flex-1">{site.name}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

function FamilySiteIcon({ site }: { site: PublicFamilySite }) {
  if (site.logoType === 'emoji' && site.logoEmoji) {
    return (
      <span className="size-4 shrink-0 grid place-items-center text-base leading-none">
        {site.logoEmoji}
      </span>
    );
  }
  if (site.logoType === 'lucide') {
    const Icon = getFamilySiteLucideIcon(site.logoLucideName);
    if (Icon) return <Icon className="size-4 shrink-0" />;
  }
  if (site.logoType === 'image' && site.logoUrl) {
    return <img src={site.logoUrl} alt="" className="size-4 shrink-0 rounded-sm object-contain" />;
  }
  return <Globe className="size-4 shrink-0" />;
}
